/**
 * A logo file's intrinsic proportions.
 *
 * Moved out of `logo-editor.js` (LN-4b) so the brand-kit apply can measure a
 * downloaded kit asset without importing the editor's whole bridge/picker
 * graph. Behaviour is verbatim; only the file changed.
 *
 * WHY THIS IS LOAD-BEARING. `applyLogoAspectRatio` is a silent no-op on a falsy
 * ratio, which leaves the PREVIOUS logo's ratio pinned on the slot — the exact
 * failure `logo-normalizer.js` documents, where every new mark is letterboxed
 * into a box built for the one it replaced. Every caller that swaps a source
 * must measure first.
 */

/** @returns {Promise<{width:number,height:number}|null>} */
export async function readIntrinsicDimensions(fileOrBlob, filename = '') {
  if (!fileOrBlob) return null;
  const type = String(fileOrBlob.type || '').toLowerCase();
  const name = String(fileOrBlob.name || filename || '').toLowerCase();
  if (type.includes('svg') || name.endsWith('.svg')) {
    try {
      return readSvgDimensions(await fileOrBlob.text());
    } catch {
      // Expected flow: an SVG that will not decode falls through to "unknown",
      // and the caller leaves the slot ratio alone rather than pinning a lie.
      return null;
    }
  }
  return readRasterDimensions(fileOrBlob);
}

/** `width`/`height` attributes first, then the `viewBox`. */
export function readSvgDimensions(svgText) {
  if (typeof DOMParser !== 'function') return null;
  const doc = new DOMParser().parseFromString(String(svgText || ''), 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.tagName.toLowerCase() !== 'svg') return null;
  const width = readPositiveNumber(svg.getAttribute('width'));
  const height = readPositiveNumber(svg.getAttribute('height'));
  if (width && height) return { width, height };

  const viewBox = svg.getAttribute('viewBox') || '';
  const parts = viewBox
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (
    parts.length === 4 &&
    Number.isFinite(parts[2]) &&
    Number.isFinite(parts[3]) &&
    parts[2] > 0 &&
    parts[3] > 0
  ) {
    return { width: parts[2], height: parts[3] };
  }
  return null;
}

export function readRasterDimensions(blob) {
  if (
    typeof Image !== 'function' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    const done = (value) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Expected flow: the URL was already revoked; nothing to recover.
      }
      resolve(value);
    };
    img.onload = () =>
      done({
        width: img.naturalWidth || img.width || 0,
        height: img.naturalHeight || img.height || 0
      });
    img.onerror = () => done(null);
    img.src = url;
  });
}

export function readPositiveNumber(value) {
  const match = String(value || '')
    .trim()
    .match(/^([0-9]*\.?[0-9]+)/);
  if (!match) return null;
  const num = Number.parseFloat(match[1]);
  return Number.isFinite(num) && num > 0 ? num : null;
}
